import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
} from '@mui/material';
import { Provider, GcpNetworkType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { AvailabilityZoneSelect } from '@/components/regions/availabilityZoneSelect';
import { GcpNetworkSelect } from '@/components/regions/gcpNetworkSelect';
import { GcpSubnetSelect } from '@/components/regions/gcpSubnetSelect';
import { RestoreRegionSelect } from '@/components/regions/restoreRegionSelect';
import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpCloudSqlState } from '../../restoreGcpCloudSqlWizard';
import { EonTagRemark } from '../eonTagRemark';

export const GcpCloudSqlConfiguration = (
  props: StepProps<RestoreGcpCloudSqlState>
) => {
  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [networkType] = useState(GcpNetworkType.GCP_NETWORK_TYPE_PRIVATE);
  const dal = useDAL();

  // const isPublicNetworkType =
  //   networkType === RestoreGcpCloudSqlRequestNetworkTypeEnum.Public;
  const isPrivateNetworkType =
    networkType === GcpNetworkType.GCP_NETWORK_TYPE_PRIVATE;

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const labels = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon-restore': 'true',
    };

    return dal.restore.gcpCloudSql
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          gcpCloudSql: {
            zone: props.wizardState.zone!,
            name: props.wizardState.resourceName ?? '',
            networkName: isPrivateNetworkType
              ? props.wizardState.networkName
              : undefined,
            subnetName: isPrivateNetworkType
              ? props.wizardState.subnetName
              : undefined,
            networkHostProject: isPrivateNetworkType
              ? props.wizardState.networkHostProject
              : undefined,
            networkType,
            labels,
          },
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.resourceName}`
        )
      )
      .catch((e) => {
        setIsRestoring(false);
        setError(`Failed to restore DB: ${e.message}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          isRestoring ||
          !props.wizardState.resourceName ||
          !props.wizardState.regionName ||
          !props.wizardState.zone
        ) {
          return false;
        }

        if (isPrivateNetworkType) {
          return (
            !!props.wizardState.networkName &&
            !!props.wizardState.subnetName &&
            !!props.wizardState.networkHostProject
          );
        }

        return true;
      }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark provider={Provider.Gcp} />
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>Instance ID*</FormLabel>
            <TransactionalTextField
              initValue={props.wizardState.resourceName}
              onChange={(resourceName) =>
                props.setWizardState((state) => ({ ...state, resourceName }))
              }
            />
          </FormControl>
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Gcp}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(regionName) =>
              props.setWizardState((state) => ({
                ...state,
                zone: undefined,
                subnetName: undefined,
                networkName: undefined,
                networkHostProject: undefined,
                isSharedNetwork: undefined,
                regionName,
              }))
            }
          />
          <AvailabilityZoneSelect
            isRequired
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            availabilityZone={props.wizardState.zone}
            cloudProvider={Provider.Gcp}
            onChange={(zone) =>
              props.setWizardState((state) => ({
                ...state,
                zone,
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <GcpNetworkSelect
            title='Network*'
            allowSharedNetworks
            allowOnlyPeeredNetworks
            disabledItems={[]}
            accountId={props.wizardState.restoreAccount?.id || ''}
            networkName={props.wizardState.networkName || ''}
            networkHostProject={props.wizardState.networkHostProject || ''}
            onChange={(networkName, networkHostProject, isSharedNetwork) =>
              props.setWizardState((state) => ({
                ...state,
                networkName: networkName,
                networkHostProject: networkHostProject,
                isSharedNetwork: isSharedNetwork,
                subnetName: undefined,
              }))
            }
          />
          <FormControl size='small' className='flex-1'>
            <FormLabel>Subnet name*</FormLabel>
            <GcpSubnetSelect
              isSharedNetwork={props.wizardState.isSharedNetwork}
              accountId={props.wizardState.restoreAccount?.id || ''}
              region={props.wizardState.regionName || ''}
              networkName={props.wizardState.networkName}
              subnetName={props.wizardState.subnetName || ''}
              onChange={(subnetName) =>
                props.setWizardState((state) => ({ ...state, subnetName }))
              }
            />
          </FormControl>
          {/* </Stack> */}
          {/* <List className='w-full'>
            <OptionListItem
              data-testid='publicNetworkType'
              isChecked={isPublicNetworkType}
              onClick={() => {
                setNetworkType(RestoreGcpCloudSqlRequestNetworkTypeEnum.Public);
              }}
            >
              <FormControlLabel
                checked={isPublicNetworkType}
                onClick={() => {
                  setNetworkType(RestoreGcpCloudSqlRequestNetworkTypeEnum.Public);
                }}
                control={<Radio />}
                label='Public IP'
              />
            </OptionListItem>
            <OptionListItem
              data-testid='privateNetworkType'
              isChecked={isPrivateNetworkType}
              onClick={() =>
                setNetworkType(RestoreGcpCloudSqlRequestNetworkTypeEnum.Private)
              }
            >
              <FormControlLabel
                checked={isPrivateNetworkType}
                onClick={() =>
                  setNetworkType(RestoreGcpCloudSqlRequestNetworkTypeEnum.Private)
                }
                control={<Radio />}
                label='Private IP'
              />
              <Collapse className='w-full' in={isPrivateNetworkType}>
                <Stack className='w-1/2 mt-[24px]'>
                  <Stack direction='row' alignItems='center' gap='8px'>
                    <GcpSubnetSelect
                      allowOnlyPeeredNetworks
                      accountId={props.wizardState.restoreAccount?.id}
                      region={props.wizardState.regionName}
                      subnet={props.wizardState.network}
                      onChange={(network) =>
                        props.setWizardState((state) => ({
                          ...state,
                          network,
                        }))
                      }
                    />
                  </Stack>
                </Stack>
              </Collapse>
            </OptionListItem>
          </List> */}
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(keepOriginalTags) =>
            props.setWizardState((state) => ({ ...state, keepOriginalTags }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
          provider={Provider.Gcp}
        />
      </CardContent>
    </StepContainer>
  );
};
